import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiRequest from "../services/api";
import PartnerQuickModal from "../components/PartnerQuickModal";
import { showSuccess, showError } from "../components/dialogHelper";
import QrScannerModal from "../components/QrScannerModal";

import {
  FaQrcode,
  FaCreditCard,
  FaCalendarAlt,
  FaHashtag,
  FaTimes,
  FaPlus,
  FaTrash,
  FaUser,
  FaBoxOpen,
  FaCalculator,
  FaFolderPlus,
  FaEdit,
  FaEye,
  FaPlusCircle,
  FaCheckCircle,
  FaSpinner,
  FaArrowLeft,
  FaFileAlt,
} from "react-icons/fa";

// ---------------------------------------------------------------------------
// CONSTANTS & HELPERS
// ---------------------------------------------------------------------------
const GOODS_THRESHOLD = 20000;
const SERVICE_THRESHOLD = 10000;
const INDIVIDUAL_WITHHOLD_RATE = 30;

const STATUS_STYLES = {
  DRAFT: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  CONFIRMED: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  CANCELLED: "bg-red-500/10 text-red-400 border-red-500/20",
  RECEIVED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  PARTIALLY_RECEIVED: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  DELIVERED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  PARTIALLY_DELIVERED: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  PAID: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  PARTIALLY_PAID: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  RETURNED: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  PARTIALLY_RETURNED: "bg-purple-500/10 text-purple-300 border-purple-500/20",
  COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

const statusStyle = (status) =>
  STATUS_STYLES[status?.toUpperCase()] ??
  "bg-slate-800 text-slate-400 border-transparent";

const cloneItem = (item) => ({
  ...item,
  product: item.product ? { ...item.product } : { id: "" },
});

/**
 * Pure frontend mirror of backend tax / withholding / excise logic.
 * Used only for live preview in CREATE / EDIT.
 * VIEW mode always shows backend values.
 */

const computeFinancials = ({
  items,
  invoiceType,
  taxType,
  taxRate,
  withholdRate,
  vatWithholdRate,
  totGoodsRate,
  totServiceRate,
  applyExcise,
  applyVat,
  applyWithhold,
  applyVatWithhold,
  partnerIsBusiness,
  canShowIncomeWithhold,
  canShowVatWithhold,
}) => {
  let taxableBase = 0;
  let nonTaxableBase = 0;
  let taxableDiscount = 0;
  let nonTaxableDiscount = 0;
  let taxableExcise = 0;
  let nonTaxableExcise = 0;
  let tax = 0;
  let withhold = 0;
  let vatWithhold = 0;
  let goodsTotal = 0;
  let serviceTotal = 0;

  const num = (value) => {
    if (value === null || value === undefined || value === "") {
      return 0;
    }

    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  // Supports decimal rates (0.02 = 2%) and percentage rates (2 = 2%).
  const excisePercent = (value) => {
    const rate = num(value);
    return rate > 0 && rate < 1 ? rate * 100 : rate;
  };

  (items || []).forEach((item) => {
    const qty = num(item.quantity);

    const price =
      invoiceType === "PURCHASE" || invoiceType === "PURCHASE_RETURN"
        ? num(item.purchasePrice)
        : num(item.sellingPrice);

    const amount = qty * price;
    const disc = num(item.discount);
    const netAmount = amount - disc;

    const isService = item.product?.type === "SERVICE";

    // Income withholding base: discounted goods/service amounts.
    if (isService) {
      serviceTotal += netAmount;
    } else {
      goodsTotal += netAmount;
    }

    // VAT/TOT classification.
    const taxableValue =
      item.taxable != null ? item.taxable : item.product?.taxable;

    const isTaxable = taxableValue === true;

    // Excise classification is independent of VAT classification.
    const exciseTaxableValue =
      item.exciseTaxable ?? item.product?.exciseTaxable ?? taxableValue;

    const isExciseTaxable = exciseTaxableValue === true;

    const exciseRate = excisePercent(
      item.exciseRate ?? item.product?.exciseRate
    );

    let itemExcise = 0;

    // Excise is calculated on the gross amount to match
    // the saved invoice calculation.
    if (applyExcise && exciseRate > 0) {
      itemExcise = (amount * exciseRate) / 100;

      if (isExciseTaxable) {
        taxableExcise += itemExcise;
      } else {
        nonTaxableExcise += itemExcise;
      }
    }

    // Keep gross amounts and discounts separate.
    if (isTaxable) {
      taxableBase += amount;
      taxableDiscount += disc;
    } else {
      nonTaxableBase += amount;
      nonTaxableDiscount += disc;
    }

    // VAT/TOT base: gross amount minus discount,
    // plus excise when the excise is taxable.
    if (applyVat && isTaxable) {
      let rate = 0;

      if (taxType === "VAT") {
        // Use the item’s own rate when it is explicitly present (including 0 for VAT0).
        // Fall back to the tenant rate only when the item has no rate at all.
        rate =
          item.taxRate != null && item.taxRate !== ""
            ? num(item.taxRate)
            : num(taxRate);
      } else if (taxType === "TOT") {
        rate = isService ? num(totServiceRate) : num(totGoodsRate);
      }

      const taxBase = netAmount + (isExciseTaxable ? itemExcise : 0);
      tax += (taxBase * rate) / 100;
    }
  });

  // Final amounts include excise and deduct their respective discounts.
  const taxableAmount = taxableBase + taxableExcise - taxableDiscount;

  const nonTaxableAmount =
    nonTaxableBase + nonTaxableExcise - nonTaxableDiscount;

  const discount = taxableDiscount + nonTaxableDiscount;
  const excise = taxableExcise + nonTaxableExcise;

  // Gross item amount before discounts and excise.
  const grossAmount = taxableBase + nonTaxableBase;

  // Both amounts already include excise.
  const subtotal = taxableAmount + nonTaxableAmount;

  // Income withholding.
  if (applyWithhold && canShowIncomeWithhold) {
    const parsedRate = Number(withholdRate);

    const rate = partnerIsBusiness
      ? withholdRate != null &&
        withholdRate !== "" &&
        Number.isFinite(parsedRate)
        ? parsedRate
        : 0
      : INDIVIDUAL_WITHHOLD_RATE;

    if (goodsTotal >= GOODS_THRESHOLD) {
      withhold += (goodsTotal * rate) / 100;
    }

    if (serviceTotal >= SERVICE_THRESHOLD) {
      withhold += (serviceTotal * rate) / 100;
    }
  }

  // VAT withholding is calculated on VAT only.
  if (applyVat && applyVatWithhold && canShowVatWithhold && taxType === "VAT") {
    vatWithhold = (tax * num(vatWithholdRate)) / 100;
  }

  // Excise is already included in subtotal.
  const total = subtotal + tax;
  const payable = total - withhold - vatWithhold;

  return {
    grossAmount,
    subtotal,
    taxableAmount,
    nonTaxableAmount,
    taxableExcise,
    nonTaxableExcise,
    discount,
    excise,
    tax,
    withhold,
    vatWithhold,
    total,
    payable,
  };
};

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------
const InvoiceCreate = ({
  isOpen,
  onClose,
  invoice = null,
  invoiceType = "SALE",
  initialMode = "CREATE",
  onSaveSuccess,
}) => {
  // ── UI / mode ────────────────────────────────────────────────────────────
  const [currentMode, setCurrentMode] = useState(initialMode);
  // Wizard only for MEMO: step 1 = pick CRE/DEB, step 2 = rest of form.
  // Type (SALE_RETURN / PURCHASE_RETURN) always comes from invoiceType prop.
  const [wizardStep, setWizardStep] = useState(initialMode === "MEMO" ? 1 : 2);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProcessAction, setSelectedProcessAction] = useState(null);
  const [savingPartner, setSavingPartner] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  // ── Lookups ──────────────────────────────────────────────────────────────
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);

  // ── Tenant rates (from localStorage user) ────────────────────────────────
  const [taxRate, setTaxRate] = useState(0);
  const [withholdRate, setWithholdRate] = useState(0);
  const [vatWithholdRate, setVatWithholdRate] = useState(0);
  const [totGoodsRate, setTotGoodsRate] = useState(0);
  const [totServiceRate, setTotServiceRate] = useState(0);
  const [tenantTaxType, setTenantTaxType] = useState("VAT");
  const [fiscalType, setFiscalType] = useState("MANUAL");
  const [action, setAction] = useState("DRAFT");
  const [isWithholdingAgentTenant, setIsWithholdingAgentTenant] =
    useState(false);
  const [applyTax, setApplyTax] = useState(false);
  const [applyExcise, setApplyExcise] = useState(false);
  // ── Partner-derived flags ────────────────────────────────────────────────
  const [partnerIsBusiness, setPartnerIsBusiness] = useState(false);
  const [partnerIsWithholdingAgent, setPartnerIsWithholdingAgent] =
    useState(false);
  const [partnerTaxType, setPartnerTaxType] = useState("VAT");

  const getCurrentDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60 * 1000)
      .toISOString()
      .slice(0, 16);
  };

  // ── Backend financial snapshot (VIEW mode) ───────────────────────────────
  const [backendFinancials, setBackendFinancials] = useState({
    grossAmount: 0,
    subtotal: 0,
    nonTaxableAmount: 0,
    tax: 0,
    excise: 0,
    nonTaxableExcise: 0,
    withhold: 0,
    vatWithhold: 0,
    totalAmount: 0,
    balanceAmount: 0,
    paidAmount: 0,
    changeAmount: 0,
    discount: 0,
    profit: 0,
  });

  // ── Form state ───────────────────────────────────────────────────────────
  const [invoiceData, setInvoiceData] = useState({
    invoiceDate: getCurrentDateTime(),
    partner: { id: "" },
    parentInvoice: { id: "" },
    irn: "",
    mrc: "",
    note: "",
    docType: "INV",
    type: invoiceType,
    fiscalType: "MANUAL",
    applyExcise: false,
    applyVat: false,
    applyWithhold: false,
    applyVatWithhold: false,
    whReceiptNumber: "",
    vatWhReceiptNumber: "",
    paymentMethod: "BANK",
    paymentTerm: "IMMIDIATE",
    reference: "",
    bankCommission: 0,
    email: "",
    mobile: "",
    items: [],
  });

  const isNewInvoice = currentMode === "CREATE";
  const isNewMemo = currentMode === "MEMO";
  // True only when editing an existing draft (not when creating a memo from a parent)
  const isUpdatingExisting = currentMode === "EDIT" && !!invoice?.id;
  // Load parent / existing invoice data for EDIT, MEMO, or VIEW.
  // Based on initialMode so changing currentMode after load does not re-trigger hydrate.
  const shouldLoadExistingInvoice =
    !!invoice?.id &&
    (initialMode === "EDIT" ||
      initialMode === "MEMO" ||
      initialMode === "VIEW");

  // Parent type for MEMO → return type (prefer loaded invoice, fall back to prop)
  const parentTypeForMemo = (
    invoice?.type ||
    invoiceType ||
    "SALE"
  ).toUpperCase();
  const memoType =
    parentTypeForMemo === "PURCHASE" || parentTypeForMemo === "PURCHASE_RETURN"
      ? "PURCHASE_RETURN"
      : "SALE_RETURN";

  const statusUpper = (
    invoiceData.status ||
    invoice?.status ||
    ""
  ).toUpperCase();
  const isDraft = statusUpper === "DRAFT";

  const docTypeUpper = (
    invoiceData.docType ||
    invoice?.docType ||
    "INV"
  ).toUpperCase();
  const typeUpper = (invoiceData.type || invoiceType || "").toUpperCase();

  const isReturnType =
    typeUpper === "SALE_RETURN" || typeUpper === "PURCHASE_RETURN";

  // Original tax invoice (INV), not a return document
  const isOriginalInvoice =
    !isReturnType &&
    (docTypeUpper === "INV" ||
      typeUpper === "SALE" ||
      typeUpper === "PURCHASE");

  // Parent was an original SALE/PURCHASE (used for MEMO editability after we rewrite type to *_RETURN)
  const parentWasOriginal =
    initialMode === "MEMO" &&
    (parentTypeForMemo === "SALE" || parentTypeForMemo === "PURCHASE");

  // MEMO from a posted original invoice
  const memoFromPostedOriginal =
    currentMode === "MEMO" &&
    parentWasOriginal &&
    (statusUpper === "PAID" ||
      statusUpper === "PARTIALLY_RETURNED" ||
      statusUpper === "PARTIALLY_PAID" ||
      statusUpper === "COMPLETED" ||
      statusUpper === "DELIVERED" ||
      statusUpper === "PARTIALLY_DELIVERED");

  // MEMO / edit of a draft return document
  const memoFromDraftReturn =
    currentMode === "MEMO" && isReturnType && statusUpper === "DRAFT";

  const editable =
    currentMode === "CREATE" ||
    (currentMode === "EDIT" && isDraft) ||
    memoFromPostedOriginal ||
    memoFromDraftReturn;

  const fiscalFieldsEditable = editable;

  const canSave =
    currentMode === "CREATE" ||
    (currentMode === "EDIT" && isDraft) ||
    memoFromPostedOriginal ||
    memoFromDraftReturn;

  // Treat return types like their base for UI / tax direction
  const isSale =
    invoiceData.type === "SALE" || invoiceData.type === "SALE_RETURN";
  const isPurchase =
    invoiceData.type === "PURCHASE" || invoiceData.type === "PURCHASE_RETURN";

  // Effective tax type (matches backend resolveTaxType)
  const effectiveTaxType = isSale ? tenantTaxType : partnerTaxType;

  // Visibility flags (matches backend shouldApply* helpers)
  const showApplyVat = effectiveTaxType === "VAT" || effectiveTaxType === "TOT";
  const showApplyExcise = effectiveTaxType === "VAT";

  // SALE → partner is withholding agent (backend also checks isBusiness for income WH)
  // PURCHASE → tenant is withholding agent
  const canShowIncomeWithhold = isSale
    ? partnerIsBusiness // backend further ANDs with partner.isWithholdingAgent on save
    : true; // purchase always allows the attempt

  const canShowVatWithhold =
    effectiveTaxType === "VAT" &&
    showApplyVat &&
    ((isSale && partnerIsWithholdingAgent) ||
      (isPurchase && isWithholdingAgentTenant));

  // Threshold eligibility for income WH
  const { goodsAmount, serviceAmount, withholdingEligible } = useMemo(() => {
    let goods = 0;
    let services = 0;
    invoiceData.items.forEach((item) => {
      const price =
        invoiceData.type === "PURCHASE" ||
        invoiceData.type === "PURCHASE_RETURN"
          ? Number(item.purchasePrice) || 0
          : Number(item.sellingPrice) || 0;
      const amt = price * (Number(item.quantity) || 0);
      if (item.product?.type === "SERVICE") services += amt;
      else goods += amt;
    });
    return {
      goodsAmount: goods,
      serviceAmount: services,
      withholdingEligible:
        goods >= GOODS_THRESHOLD || services >= SERVICE_THRESHOLD,
    };
  }, [invoiceData.items, invoiceData.type]);

  // Live financials
  const displayFinancials = useMemo(() => {
    if (isUpdatingExisting && currentMode === "VIEW") {
      const taxableExcise = Number(backendFinancials.excise) || 0;
      const nonTaxableExcise = Number(backendFinancials.nonTaxableExcise) || 0;
      const discount = Number(backendFinancials.discount) || 0;
      const apiSubtotal = Number(backendFinancials.subtotal) || 0;
      const apiNonTaxable = Number(backendFinancials.nonTaxableAmount) || 0;

      const taxableAmount = apiSubtotal + taxableExcise; // 44 840
      const nonTaxableAmount = apiNonTaxable + nonTaxableExcise; // 59 080
      const grossAmount = apiSubtotal + apiNonTaxable + discount; // 96 000
      const subtotal = taxableAmount + nonTaxableAmount; // 103 920 (same as live)

      return {
        grossAmount,
        subtotal,
        taxableAmount,
        nonTaxableAmount,
        taxableExcise,
        nonTaxableExcise,
        discount,
        excise: taxableExcise + nonTaxableExcise, // 10 320
        tax: Number(backendFinancials.tax) || 0, // 6 726
        withhold: Number(backendFinancials.withhold) || 0, // 2 808
        vatWithhold: Number(backendFinancials.vatWithhold) || 0,
        total: Number(backendFinancials.totalAmount) || 0, // 110 646
        payable: Number(backendFinancials.balanceAmount) || 0, // 107 838
      };
    }

    // CREATE / EDIT – keep the pure frontend calculation
    return computeFinancials({
      items: invoiceData.items,
      invoiceType: invoiceData.type,
      taxType: effectiveTaxType,
      taxRate,
      withholdRate,
      vatWithholdRate,
      totGoodsRate,
      totServiceRate,
      applyExcise: invoiceData.applyExcise,
      applyVat: invoiceData.applyVat,
      applyWithhold: invoiceData.applyWithhold,
      applyVatWithhold: invoiceData.applyVatWithhold,
      partnerIsBusiness,
      canShowIncomeWithhold,
      canShowVatWithhold,
    });
  }, [
    isUpdatingExisting,
    currentMode,
    backendFinancials,
    invoiceData,
    effectiveTaxType,
    taxRate,
    withholdRate,
    vatWithholdRate,
    totGoodsRate,
    totServiceRate,
    partnerIsBusiness,
    canShowIncomeWithhold,
    canShowVatWithhold,
  ]);

  useEffect(() => {
    setInvoiceData((prev) => {
      if (prev.invoiceDate) return prev;
      return {
        ...prev,
        invoiceDate: getCurrentDateTime(),
      };
    });
  }, []);

  // ── Load lookups ─────────────────────────────────────────────────────────
  const loadLookups = useCallback(async (contextType) => {
    try {
      const clientParams = new URLSearchParams({
        type: contextType === "SALE" ? "CUSTOMER" : "SUPPLIER",
        keyword: "",
        page: "0",
        size: "200",
      });
      const productParams = new URLSearchParams({
        keyword: "",
        page: "0",
        size: "200",
      });
      const [partnersRes, productsRes] = await Promise.all([
        apiRequest(`clients?${clientParams}`),
        apiRequest(`products?${productParams}`),
      ]);
      setPartners(partnersRes?.data?.content || []);
      setProducts(productsRes?.data?.content || []);
    } catch (err) {
      setError(err?.message || "Failed to load partners / products.");
    }
  }, []);

  const toDatetimeLocal = (isoString) => {
    if (!isoString) return "";
    return isoString.slice(0, 16);
  };

  // ── Hydrate on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const hydrate = async () => {
      setError("");
      setSelectedProcessAction(null);
      setIsLoading(true);
      // MEMO starts at step 1 (pick CRE/DEB); everything else at step 2
      setWizardStep(initialMode === "MEMO" ? 1 : 2);

      // ── Tenant rates from localStorage (use LOCAL vars — setState is async) ──
      let tenantTax = false;
      let tenantExcise = false;
      let tenantWithholdingAgent = false;
      let nextTaxRate = 0;
      let nextWithholdRate = 0;
      let nextVatWithholdRate = 0;
      let nextTotGoodsRate = 0;
      let nextTotServiceRate = 0;
      let nextTenantTaxType = "VAT";
      let nextFiscalType = "MANUAL";

      try {
        const parsed = JSON.parse(localStorage.getItem("user") || "{}");
        const userData = parsed?.data || {};
        const t = userData.tenant || {};

        nextTaxRate = Number(t.taxRate || 0);
        nextWithholdRate = Number(t.withholdRate || 0);
        nextVatWithholdRate = Number(t.vatWithholdRate || 0);
        nextTotGoodsRate = Number(t.totGoodsRate || 0);
        nextTotServiceRate = Number(t.totServiceRate || 0);
        nextTenantTaxType = t.taxType || "VAT";
        nextFiscalType = t.fiscalType || "MANUAL";
        tenantTax = Boolean(t.tax);
        tenantExcise = Boolean(t.tenantExcise);
        tenantWithholdingAgent = Boolean(t.withholdingAgent);
      } catch {
        // keep defaults
      }

      setTaxRate(nextTaxRate);
      setWithholdRate(nextWithholdRate);
      setVatWithholdRate(nextVatWithholdRate);
      setTotGoodsRate(nextTotGoodsRate);
      setTotServiceRate(nextTotServiceRate);
      setTenantTaxType(nextTenantTaxType);
      setFiscalType(nextFiscalType);
      setApplyTax(tenantTax);
      setApplyExcise(tenantExcise);
      setIsWithholdingAgentTenant(tenantWithholdingAgent);

      const contextType = invoice?.type || invoiceType;

      // Partners
      try {
        const clientParams = new URLSearchParams({
          type: contextType === "SALE" ? "CUSTOMER" : "SUPPLIER",
          keyword: "",
          page: "0",
          size: "200",
        });
        const partnersRes = await apiRequest(`clients?${clientParams}`);
        setPartners(partnersRes?.data?.content || []);
      } catch (err) {
        setError(err?.message || "Failed to load partners.");
      }

      // Products (needed to resolve product.type for TOT / WH)
      let productsList = [];
      try {
        const productParams = new URLSearchParams({
          keyword: "",
          page: "0",
          size: "200",
        });
        const productsRes = await apiRequest(`products?${productParams}`);
        productsList = productsRes?.data?.content || [];
        setProducts(productsList);
      } catch (err) {
        setError(err?.message || "Failed to load products.");
      }

      if (shouldLoadExistingInvoice) {
        let data = invoice;

        // Always re-fetch full details for EDIT / MEMO / VIEW so we have
        // complete items, partner flags, and financials from the backend.
        try {
          const response = await apiRequest(`invoices/${invoice.id}`);
          data = response?.data;
        } catch (err) {
          setError("Could not load invoice details.");
          setIsLoading(false);
          return;
        }

        // Backend financials (VIEW source of truth)
        setBackendFinancials({
          grossAmount: 0, // no longer used directly
          subtotal: Number(data.subtotal) || 0,
          nonTaxableAmount: Number(data.nonTaxableAmount) || 0,
          tax: Number(data.tax) || 0,
          excise: Number(data.excise) || 0,
          nonTaxableExcise: Number(data.nonTaxableExcise) || 0,
          withhold: Number(data.withhold) || 0,
          vatWithhold: Number(data.vatWithhold) || 0,
          totalAmount: Number(data.totalAmount) || 0,
          balanceAmount: Number(data.balanceAmount) || 0,
          paidAmount: Number(data.paidAmount) || 0,
          changeAmount: Number(data.changeAmount) || 0,
          discount: Number(data.discount) || 0,
          profit: Number(data.profit) || 0,
        });

        // Detect fiscalType when backend does not return it
        let detectedFiscalType = data.fiscalType || "MANUAL";
        if (data.irn && data.mrc) {
          detectedFiscalType = "MACHINE";
        } else {
          detectedFiscalType = data.fiscalType || "MANUAL";
        }

        // For MEMO, force return type from parent (SALE → SALE_RETURN, PURCHASE → PURCHASE_RETURN)
        const loadedParentType = (
          data.type ||
          invoiceType ||
          "SALE"
        ).toUpperCase();
        const resolvedMemoType =
          loadedParentType === "PURCHASE" ||
          loadedParentType === "PURCHASE_RETURN"
            ? "PURCHASE_RETURN"
            : "SALE_RETURN";

        setInvoiceData({
          id: data.id,
          invoiceDate: toDatetimeLocal(data.invoiceDate),
          partner: {
            id: data.partnerId ? String(data.partnerId) : "",
          },
          parentInvoice: {
            id:
              initialMode === "MEMO"
                ? String(data.id) // parent is this invoice when creating a memo
                : data.parentInvoiceId
                ? String(data.parentInvoiceId)
                : "",
          },
          note: data.note || "",
          type:
            initialMode === "MEMO"
              ? resolvedMemoType
              : data.type || invoiceType,
          status: data.status || "DRAFT",
          irn: data.irn || "",
          mrc: data.mrc || "",
          docType:
            initialMode === "MEMO" && (data.docType || "INV") === "INV"
              ? "CRE"
              : data.docType || "INV",
          fiscalType: detectedFiscalType,
          applyExcise: Boolean(data.applyExcise),
          applyVat: Boolean(data.applyVat),
          applyWithhold: Boolean(data.applyWithhold),
          applyVatWithhold: Boolean(data.applyVatWithhold),
          whReceiptNumber: data.whReceiptNumber || data.whReceipt || "",
          vatWhReceiptNumber: data.vatWhReceiptNumber || data.vwhReceipt || "",
          paymentMethod: data.paymentMethod || "BANK",
          paymentTerm: data.term || "IMMIDIATE",
          reference: data.reference || "",
          bankCommission: data.bankCommission || 0,
          email: data.email || "",
          mobile: data.mobile || "",
          items: (data.items || []).map((item) => {
            const found = productsList.find(
              (p) => String(p.id) === String(item.productId)
            );
            return {
              id: item.id,
              productId: item.productId ? String(item.productId) : "",
              product: {
                id: item.productId ? String(item.productId) : "",
                name: item.productName || found?.name || "",
                type: found?.type || item.productType || "GOODS",
                ...(found || {}),
              },
              quantity: item.quantity ?? 1,
              discount: item.discount ?? 0,
              purchasePrice: item.purchasePrice ?? 0,
              sellingPrice: item.sellingPrice ?? 0,
              batchNumber: item.batchNumber ?? null,
              expiryDate: item.expiryDate ?? null,
              taxRate: item.taxRate ?? found?.taxRate,
              exciseRate: item.exciseRate ?? found?.exciseRate ?? 0,
              excise: item.excise || 0,
              tax: item.tax || 0,
              withhold: item.withhold || 0,
              vatWithhold: item.vatWithhold || 0,
              totalCost: item.totalCost || 0,
              totalPrice: item.totalPrice || 0,
            };
          }),
        });

        setPartnerIsBusiness(
          Boolean(data.partnerIsBusiness || data.partnerIsGovernment)
        );

        // Force VIEW if no longer a draft
        if (
          data.status?.toUpperCase() !== "DRAFT" &&
          initialMode !== "MEMO" &&
          initialMode !== "VIEW"
        ) {
          setCurrentMode("VIEW");
        } else {
          setCurrentMode(initialMode);
        }
      } else {
        // Fresh create — use LOCAL tenant flags (not stale React state)
        setBackendFinancials({
          grossAmount: 0,
          subtotal: 0,
          excise: 0,
          tax: 0,
          withhold: 0,
          vatWithhold: 0,
          totalAmount: 0,
          balanceAmount: 0,
          paidAmount: 0,
          changeAmount: 0,
          discount: 0,
          profit: 0,
        });

        setInvoiceData({
          invoiceDate: getCurrentDateTime(),
          partner: { id: "" },
          parentInvoice: { id: "" },
          note: "",
          type: invoiceType,
          applyExcise: tenantExcise,
          applyVat: tenantTax,

          applyWithhold: tenantWithholdingAgent,

          applyVatWithhold: false,

          whReceiptNumber: "",
          vatWhReceiptNumber: "",
          paymentMethod: "BANK",
          paymentTerm: "IMMIDIATE",
          reference: "",
          bankCommission: 0,
          email: "",
          mobile: "",
          items: [],
          irn: "",
          mrc: "",
          docType: "INV",
          fiscalType: "MANUAL",
        });

        setPartnerIsBusiness(false);
        setPartnerIsWithholdingAgent(false);
        setPartnerTaxType("VAT");
        setCurrentMode("CREATE");
      }

      setIsLoading(false);
    };

    hydrate();
  }, [
    isOpen,
    invoice,
    initialMode,
    shouldLoadExistingInvoice,
    invoiceType,
    loadLookups,
  ]);

  // After partners are loaded, sync partner flags for an existing invoice
  useEffect(() => {
    // Sync partner flags for any loaded existing invoice (EDIT / MEMO / VIEW)
    if (
      !shouldLoadExistingInvoice ||
      !invoiceData.partner?.id ||
      partners.length === 0
    )
      return;
    const selected = partners.find(
      (p) => String(p.id) === String(invoiceData.partner.id)
    );
    if (selected) {
      setPartnerIsBusiness(Boolean(selected.business || selected.government));
      setPartnerIsWithholdingAgent(Boolean(selected.withholdingAgent));
      setPartnerTaxType(selected.taxType || "VAT");
    }
  }, [partners, shouldLoadExistingInvoice, invoiceData.partner?.id]);

  // ── Item helpers ─────────────────────────────────────────────────────────

  const addNewItemLine = () => {
    if (!editable) return;
    setInvoiceData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: "",
          product: { id: "" },
          quantity: 1,
          discount: 0,
          purchasePrice: 0,
          sellingPrice: 0,
          batchNumber: "",
          expiryDate: "",
        },
      ],
    }));
  };

  useEffect(() => {
    const isSaleSide =
      invoiceData.type === "SALE" || invoiceData.type === "SALE_RETURN";
    const isPurchaseSide =
      invoiceData.type === "PURCHASE" || invoiceData.type === "PURCHASE_RETURN";

    const effectiveFiscalType = isPurchaseSide
      ? invoiceData.fiscalType || "MANUAL"
      : fiscalType;

    const isSaleEims = isSaleSide && effectiveFiscalType === "EIMS";
    const needsMrc = effectiveFiscalType === "MACHINE";

    setInvoiceData((prev) => {
      const nextNewIrn = isSaleEims;
      const nextIrn = isSaleEims ? "" : prev.irn;
      const nextMrc = needsMrc ? prev.mrc : "";
      const nextFiscalType = isPurchaseSide
        ? prev.fiscalType || "MANUAL"
        : prev.fiscalType;

      if (
        prev.newIrn === nextNewIrn &&
        prev.irn === nextIrn &&
        prev.mrc === nextMrc &&
        prev.fiscalType === nextFiscalType
      ) {
        return prev;
      }

      return {
        ...prev,
        newIrn: nextNewIrn,
        irn: nextIrn,
        mrc: nextMrc,
        fiscalType: nextFiscalType,
      };
    });
  }, [invoiceData.type, invoiceData.fiscalType, fiscalType]);

  const handleItemFieldChange = (index, field, value) => {
    if (!editable) return;

    setInvoiceData((prev) => {
      const items = prev.items.map((item, i) =>
        i === index ? cloneItem(item) : item
      );

      if (field === "productId") {
        const found = products.find((p) => String(p.id) === String(value));
        if (found) {
          items[index].productId = String(found.id);
          items[index].product = found;
          items[index].purchasePrice = found.purchasePrice ?? 0;
          items[index].sellingPrice = found.sellingPrice ?? 0;
          items[index].expiryDate = found.expiryDate ?? "";
          items[index].batchNumber = found.batchNumber ?? "";

          // Keep the real value (or undefined). Do NOT default to 0.
          items[index].taxRate = found.taxRate;
          items[index].exciseRate = found.exciseRate;

          // Optional but recommended – copy the flags so isTaxable works even if product is later mutated
          items[index].taxable = found.taxable;
          items[index].exciseTaxable = found.exciseTaxable;
        } else {
          items[index].productId = String(value);
          items[index].product = { id: String(value) };
        }
      } else if (
        ["quantity", "purchasePrice", "sellingPrice"].includes(field)
      ) {
        items[index][field] = value === "" ? "" : parseFloat(value) || 0;
      } else {
        items[index][field] = value;
      }
      return { ...prev, items };
    });
  };

  const dropItemLine = (index) => {
    if (!editable) return;
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // ── Tax / WH toggles ─────────────────────────────────────────────────────
  const handleApplyVatChange = (checked) => {
    setInvoiceData((prev) => {
      const next = { ...prev, applyVat: checked };
      if (!checked) {
        next.applyVatWithhold = false;
      }
      return next;
    });
  };

  const handleApplyExciseChange = (checked) => {
    setInvoiceData((prev) => ({
      ...prev,
      applyExcise: checked,
    }));
  };

  const handleApplyWithholdChange = (checked) => {
    setInvoiceData((prev) => ({
      ...prev,
      applyWithhold: checked,
    }));
  };

  const handleApplyVatWithholdChange = (checked) => {
    setInvoiceData((prev) => ({
      ...prev,
      applyVatWithhold: checked,
    }));
  };

  // ── Partner change ───────────────────────────────────────────────────────
  const handlePartnerChange = (partnerId) => {
    const selected = partners.find((p) => String(p.id) === String(partnerId));

    setPartnerIsBusiness(Boolean(selected?.business || selected?.government));
    setPartnerIsWithholdingAgent(Boolean(selected?.withholdingAgent));
    setPartnerTaxType(selected?.taxType || "VAT");

    setInvoiceData((prev) => ({
      ...prev,
      partner: { id: partnerId },
      email: selected?.email || "",
      mobile: selected?.mobile || selected?.phone || "",
    }));
  };

  // ── Partner quick-create ─────────────────────────────────────────────────
  const handleCreatePartner = async (partner) => {
    try {
      setSavingPartner(true);
      const type =
        invoiceData.type === "SALE" || invoiceData.type === "SALE_RETURN"
          ? "CUSTOMER"
          : "SUPPLIER";
      const res = await apiRequest("clients", {
        method: "POST",
        body: { ...partner, type },
      });
      const saved = res.data;
      setPartnerIsBusiness(Boolean(saved?.business || saved?.government));
      setPartnerIsWithholdingAgent(Boolean(saved?.withholdingAgent));
      setPartnerTaxType(saved?.taxType || "NONE");
      setInvoiceData((prev) => {
        const isSaleType = prev.type === "SALE";
        return {
          ...prev,
          partner: { id: String(saved.id) },
          applyVat: applyTax,
          applyWithhold: isWithholdingAgentTenant,
          applyVatWithhold: false,
        };
      });
      setPartners((prev) => [...prev, saved]);
      setShowPartnerModal(false);
    } catch (err) {
      showError(err.message || "Unable to save partner.");
    } finally {
      setSavingPartner(false);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const executeFormSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const pendingAction = e.currentTarget?.dataset?.pendingAction || action;

    if (e.currentTarget?.dataset) {
      delete e.currentTarget.dataset.pendingAction;
    }

    if (!editable) {
      setError("This document cannot be edited in the current mode/status.");
      return;
    }
    if (!invoiceData.partner?.id) {
      setError("Partner is required.");
      return;
    }
    if (invoiceData.items.length === 0) {
      setError("Add at least one line item.");
      return;
    }

    if (pendingAction === "APPROVEANDPAY") {
      if (!invoiceData.paymentMethod) {
        setError("Payment method is required.");
        return;
      }

      if (invoiceData.paymentMethod === "BANK") {
        if (Number(invoiceData.bankCommission) < 0) {
          setError(
            "Bank commission is required and cannot be negative when payment method is BANK."
          );
          return;
        }

        if (!invoiceData.reference || !String(invoiceData.reference).trim()) {
          setError(
            "Payment reference is required when payment method is BANK."
          );
          return;
        }
      }
    }

    setIsLoading(true);

    try {
      const payload = {
        ...(isUpdatingExisting && { id: Number(invoice.id) }),
        invoiceDate: invoiceData.invoiceDate || "",
        partner: { id: Number(invoiceData.partner.id) },
        parentInvoice: isNewMemo
          ? { id: Number(invoice.id) }
          : invoiceData.parentInvoiceId
          ? { id: Number(invoiceData.parentInvoiceId) }
          : null,
        irn: invoiceData.irn || null,
        mrc: invoiceData.mrc || null,
        docType:
          initialMode === "MEMO" && (invoiceData.docType || "INV") === "INV"
            ? "CRE"
            : invoiceData.docType || "INV",
        // MEMO must submit as return type based on parent
        type: isNewMemo || initialMode === "MEMO" ? memoType : invoiceData.type,
        note: invoiceData.note,
        applyExcise: invoiceData.applyExcise,
        applyVat: invoiceData.applyVat,
        applyWithhold: invoiceData.applyWithhold,
        applyVatWithhold: invoiceData.applyVatWithhold,
        whReceiptNumber: invoiceData.whReceiptNumber || null,
        vatWhReceiptNumber: invoiceData.vatWhReceiptNumber || null,
        paymentMethod: invoiceData.paymentMethod || "BANK",
        paymentTerm: invoiceData.paymentTerm || "IMMIDIATE",
        reference: invoiceData.reference || "",
        bankCommission: invoiceData.bankCommission || 0,
        email: invoiceData.email || "",
        mobile: invoiceData.mobile || "",

        items: invoiceData.items.map((item) => ({
          // Do not send parent line-item ids when creating a new memo
          ...(!isNewMemo && item.id && { id: parseInt(item.id, 10) }),
          product: { id: parseInt(item.productId, 10) },
          quantity: parseFloat(item.quantity),
          discount: parseFloat(item.discount),
          purchasePrice: parseFloat(item.purchasePrice) || 0,
          sellingPrice: parseFloat(item.sellingPrice) || 0,
          batchNumber: item.batchNumber || null,
          expiryDate: item.expiryDate || null,
        })),
      };

      let response;

      if (pendingAction === "APPROVE") {
        response = await apiRequest("invoices/process-items", {
          method: "PUT",
          body: payload,
        });
      } else if (pendingAction === "APPROVEANDPAY") {
        response = await apiRequest("invoices/process-pay", {
          method: "PUT",
          body: payload,
        });
      } else {
        response = await apiRequest("invoices", {
          method: isUpdatingExisting ? "PUT" : "POST",
          body: payload,
        });
      }

      const saved = response?.data;
      if (saved) {
        setBackendFinancials({
          grossAmount: 0,
          subtotal: Number(saved.subtotal) || 0,
          nonTaxableAmount: Number(saved.nonTaxableAmount) || 0,
          tax: Number(saved.tax) || 0,
          excise: Number(saved.excise) || 0,
          nonTaxableExcise: Number(saved.nonTaxableExcise) || 0,
          withhold: Number(saved.withhold) || 0,
          vatWithhold: Number(saved.vatWithhold) || 0,
          totalAmount: Number(saved.totalAmount) || 0,
          balanceAmount: Number(saved.balanceAmount) || 0,
          paidAmount: Number(saved.paidAmount) || 0,
          changeAmount: Number(saved.changeAmount) || 0,
          discount: Number(saved.discount) || 0,
          profit: Number(saved.profit) || 0,
        });
      }

      onSaveSuccess?.();
      handleModalClose();
    } catch (err) {
      setError(err?.message || "Failed to save invoice.");
    } finally {
      setIsLoading(false);
      setAction("SAVE");
    }
  };

  // ── Workflow actions ─────────────────────────────────────────────────────
  const handleExecuteWorkflowAction = async (actionType) => {
    setError("");
    setIsLoading(true);
    setSelectedProcessAction(actionType);
    try {
      await apiRequest(`invoices/${invoice.id}/delete?action=${actionType}`, {
        method: "PUT",
      });
      onSaveSuccess?.();
      handleModalClose();
    } catch (err) {
      setError(err?.message || `Failed to execute action: ${actionType}.`);
    } finally {
      setIsLoading(false);
      setSelectedProcessAction(null);
    }
  };

  const handleModalClose = () => {
    setError("");
    setSelectedProcessAction(null);
    onClose();
  };

  // ── Labels ───────────────────────────────────────────────────────────────
  const taxLabel = (() => {
    if (effectiveTaxType === "VAT") return `VAT on Taxable (${taxRate}%)`;
    if (effectiveTaxType === "TOT") {
      const hasGoods = invoiceData.items.some(
        (i) => i.product?.type === "GOODS"
      );
      const hasService = invoiceData.items.some(
        (i) => i.product?.type === "SERVICE"
      );
      if (hasGoods && hasService)
        return `TOT (Goods ${totGoodsRate}% • Service ${totServiceRate}%)`;
      if (hasGoods) return `TOT (Goods ${totGoodsRate}%)`;
      if (hasService) return `TOT (Service ${totServiceRate}%)`;
      return "Turnover Tax";
    }
    return "";
  })();

  const RateCard = ({ title, value }) => (
    <div className="rounded-xl border border-white/10 bg-slate-950/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {title}
      </div>
      <div className="mt-1 text-sm font-bold text-emerald-400">{value}</div>
    </div>
  );

  // cash register and QrScannerModal
  const [showScanner, setShowScanner] = useState(false);
  const [notice, setNotice] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const searchProducts = useCallback(
    (term) => {
      const search = term.trim().toLowerCase();

      if (!search) {
        setFilteredProducts(products);
        return products;
      }

      const filtered = products.filter((product) => {
        const name = product.name?.toLowerCase() || "";
        const sku = product.sku?.toLowerCase() || "";
        const barcode = product.barcode?.toLowerCase() || "";

        return (
          name.includes(search) ||
          sku.includes(search) ||
          barcode.includes(search)
        );
      });

      setFilteredProducts(filtered);
      return filtered; // ← return the result
    },
    [products]
  );

  // Add a found product as a proper invoice line item
  const addProductToInvoice = useCallback(
    (product) => {
      if (!product || !editable) return;

      setInvoiceData((prev) => {
        // Optional: if the same product already exists, just increase qty
        const existingIndex = prev.items.findIndex(
          (item) => String(item.productId) === String(product.id)
        );

        if (existingIndex >= 0) {
          const items = prev.items.map((item, i) => {
            if (i !== existingIndex) return item;
            const nextQty = (Number(item.quantity) || 0) + 1;
            return { ...cloneItem(item), quantity: nextQty };
          });
          return { ...prev, items };
        }

        // New line – same shape as addNewItemLine + product selection
        const newItem = {
          productId: String(product.id),
          product,
          quantity: 1,
          purchasePrice: product.purchasePrice ?? 0,
          sellingPrice: product.sellingPrice ?? 0,
          batchNumber: product.batchNumber ?? "",
          expiryDate: product.expiryDate ?? "",
          taxRate: product.taxRate ?? 0,
          exciseRate: product.exciseRate ?? 0,
        };

        return {
          ...prev,
          items: [...prev.items, newItem],
        };
      });

      setNotice({
        type: "success",
        message: `Added ${product.name || "item"} to invoice.`,
      });
    },
    [editable]
  );

  // Called by the QR scanner
  const lookupAndAdd = useCallback(
    (code) => {
      const keyword = (code || "").trim();
      if (!keyword) return false;

      try {
        const results = searchProducts(keyword);

        if (results.length === 0) {
          setNotice({
            type: "error",
            message: `No product found for "${keyword}".`,
          });
          return false;
        }

        // Prefer exact barcode or SKU match
        const exactMatch = results.find(
          (p) =>
            String(p.barcode || "").toLowerCase() === keyword.toLowerCase() ||
            String(p.sku || "").toLowerCase() === keyword.toLowerCase()
        );

        const productToAdd = exactMatch || results[0];
        addProductToInvoice(productToAdd);
        return true;
      } catch {
        setNotice({ type: "error", message: "Search failed. Try again." });
        return false;
      }
    },
    [searchProducts, addProductToInvoice]
  );

  if (!isOpen) return null;

  const colCount = editable ? 5 : 4;

  // MEMO wizard: only choose CRE vs DEB — type is always derived from invoiceType
  const selectMemoDocType = (doc) => {
    setInvoiceData((prev) => ({
      ...prev,
      docType: doc,
      // Keep type from parent / prop (already set to SALE_RETURN or PURCHASE_RETURN in hydrate)
      type: memoType,
    }));
    setWizardStep(2);
  };

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 overflow-y-auto">
      {/* Sheet */}
      <div className="relative w-full max-w-5xl bg-slate-900/75 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.7)] text-slate-100 max-h-[90vh] flex flex-col">
        {/* Loading overlay */}
        {isLoading && !selectedProcessAction && (
          <div className="absolute inset-0 z-50 bg-slate-950/40 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl gap-2">
            <FaSpinner className="animate-spin text-3xl text-emerald-400" />
            <span className="text-[11px] font-mono tracking-widest text-slate-400 uppercase">
              Loading…
            </span>
          </div>
        )}

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="relative flex flex-col gap-4 pb-4 border-b border-white/10 w-full">
          <div className="flex items-start gap-3 pr-12">
            <div
              className={`p-2.5 border rounded-xl shrink-0 ${
                currentMode === "CREATE"
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                  : currentMode === "EDIT"
                  ? "bg-sky-500/10 border-sky-500/20 text-sky-400"
                  : "bg-blue-500/10 border-blue-500/20 text-blue-400"
              }`}
            >
              {currentMode === "CREATE" && <FaPlusCircle className="text-xl" />}
              {currentMode === "MEMO" && <FaEdit className="text-xl" />}
              {currentMode === "EDIT" && <FaEdit className="text-xl" />}
              {currentMode === "VIEW" && <FaEye className="text-xl" />}
            </div>

            <div className="space-y-1">
              <h2 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
                {currentMode === "CREATE" && "New"}
                {currentMode === "MEMO" && "MEMO"}
                {currentMode === "EDIT" && "Edit"}
                {currentMode === "VIEW" && "Details"}
                {wizardStep === 2 && (
                  <>
                    {" — "}
                    <span className="text-emerald-400 text-sm font-mono font-bold tracking-widest uppercase">
                      {invoiceData.docType}
                    </span>
                  </>
                )}
              </h2>

              {/* Top action buttons — hide on MEMO step 1 */}
              {wizardStep === 2 &&
                (currentMode === "CREATE" ||
                  currentMode === "MEMO" ||
                  (isUpdatingExisting && isDraft)) && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {/* Save */}
                    {canSave && (
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                          setAction("SAVE");
                          const form = document.getElementById("invoice-form");
                          if (form) {
                            form.dataset.pendingAction = "SAVE";
                            form.requestSubmit();
                          }
                        }}
                        className="px-3.5 py-2 rounded-xl border transition flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider
          bg-emerald-600/15 hover:bg-emerald-600 border-emerald-500/30 text-emerald-400 hover:text-white
          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading && action === "SAVE" ? (
                          <FaSpinner className="animate-spin text-xs" />
                        ) : (
                          <FaFolderPlus className="text-xs" />
                        )}
                        <span>Save</span>
                      </button>
                    )}

                    {/* Post */}
                    {(isNewInvoice || isNewMemo || isUpdatingExisting) &&
                      isDraft && (
                        <button
                          type="button"
                          disabled={isLoading}
                          onClick={() => {
                            if (
                              !window.confirm(
                                "Approve and post invoice, this will update stock balance"
                              )
                            ) {
                              return;
                            }
                            setAction("APPROVE");
                            const form =
                              document.getElementById("invoice-form");
                            if (form) {
                              form.dataset.pendingAction = "APPROVE";
                              form.requestSubmit();
                            }
                          }}
                          className="px-3.5 py-2 rounded-xl border transition flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider
          bg-blue-600/15 hover:bg-blue-600 border-blue-500/30 text-blue-400 hover:text-white
          disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading && action === "APPROVE" ? (
                            <FaSpinner className="animate-spin text-xs" />
                          ) : (
                            <FaCheckCircle className="text-xs" />
                          )}
                          <span>Post</span>
                        </button>
                      )}

                    {/* Post & Pay */}
                    {(isNewInvoice || isUpdatingExisting) &&
                      isDraft &&
                      invoice.docType === "INV" && (
                        <button
                          type="button"
                          disabled={
                            isLoading ||
                            fiscalType === "EIMS" ||
                            invoiceData.paymentTerm === "CREDIT"
                          }
                          onClick={() => {
                            if (
                              !window.confirm(
                                "Post invoice and pay, this will update stock balance and full payment."
                              )
                            ) {
                              return;
                            }
                            setAction("APPROVEANDPAY");
                            const form =
                              document.getElementById("invoice-form");
                            if (form) {
                              form.dataset.pendingAction = "APPROVEANDPAY";
                              form.requestSubmit();
                            }
                          }}
                          className="px-3.5 py-2 rounded-xl border transition flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider
          bg-violet-600/15 hover:bg-violet-600 border-violet-500/30 text-violet-400 hover:text-white
          disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading && action === "APPROVEANDPAY" ? (
                            <FaSpinner className="animate-spin text-xs" />
                          ) : (
                            <FaCreditCard className="text-xs" />
                          )}
                          <span>Post & Pay</span>
                        </button>
                      )}

                    {/* Void – only for existing DRAFT invoices */}
                    {isUpdatingExisting && isDraft && (
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                          if (
                            !window.confirm(
                              "Void this invoice? This cannot be undone."
                            )
                          )
                            return;
                          handleExecuteWorkflowAction("DELETE");
                        }}
                        className="px-3.5 py-2 rounded-xl border transition flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider
          bg-red-600/15 hover:bg-red-600 border-red-500/30 text-red-400 hover:text-white
          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading && selectedProcessAction === "DELETE" ? (
                          <FaSpinner className="animate-spin text-xs" />
                        ) : (
                          <FaTrash className="text-xs" />
                        )}
                        <span>Cancel</span>
                      </button>
                    )}
                  </div>
                )}
            </div>
          </div>

          {/* Close */}
          <div className="absolute top-0 right-0 z-10">
            <button
              type="button"
              onClick={handleModalClose}
              disabled={isLoading}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition text-slate-400 hover:text-white"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>

        {/* ── BODY ───────────────────────────────────────────────────────── */}
        {currentMode === "MEMO" && wizardStep === 1 ? (
          /* ── MEMO STEP 1: Credit vs Debit only ─────────────────────────── */
          <div className="overflow-y-auto py-8 flex-1 flex flex-col items-center justify-center gap-8">
            <p className="text-slate-400 text-sm text-center max-w-sm">
              Choose the memo type
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
              <button
                type="button"
                onClick={() => selectMemoDocType("CRE")}
                className="group flex flex-col items-center gap-3 p-6 rounded-2xl border border-white/10 bg-slate-950/40 hover:border-sky-500/50 hover:bg-sky-500/10 transition"
              >
                <div className="p-3 rounded-xl bg-sky-500/10 text-sky-400 group-hover:bg-sky-500/20">
                  <FaFileAlt className="text-3xl" />
                </div>
                <div className="text-center">
                  <div className="font-bold text-white text-base">
                    Credit Memo
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    CRE — reduce quantity / value
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => selectMemoDocType("DEB")}
                className="group flex flex-col items-center gap-3 p-6 rounded-2xl border border-white/10 bg-slate-950/40 hover:border-violet-500/50 hover:bg-violet-500/10 transition"
              >
                <div className="p-3 rounded-xl bg-violet-500/10 text-violet-400 group-hover:bg-violet-500/20">
                  <FaFileAlt className="text-3xl" />
                </div>
                <div className="text-center">
                  <div className="font-bold text-white text-base">
                    Debit Memo
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    DEB — increase quantity / value
                  </div>
                </div>
              </button>
            </div>
          </div>
        ) : (
          /* ── STEP 2 (or non-MEMO): full form ───────────────────────────── */
          <form
            id="invoice-form"
            onSubmit={executeFormSubmit}
            className="overflow-y-auto py-4 space-y-6 pr-1 flex-1 text-xs"
          >
            {error && (
              <div className="p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 font-medium">
                {error}
              </div>
            )}

            {/* Status strip */}
            {isUpdatingExisting && (
              <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3 flex justify-between items-center">
                <div>
                  <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500 block mb-1">
                    Status
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider border uppercase ${statusStyle(
                      invoiceData.status
                    )}`}
                  >
                    {invoiceData.status ?? "UNKNOWN"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {isDraft && (
                    <>
                      {currentMode === "VIEW" ? (
                        <button
                          type="button"
                          onClick={() => setCurrentMode("EDIT")}
                          className="px-3 py-1.5 rounded-lg bg-sky-600/10 hover:bg-sky-600 border border-sky-500/20 text-sky-400 hover:text-white transition flex items-center gap-1.5 font-semibold"
                        >
                          <FaEdit /> <span>Edit</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCurrentMode("VIEW")}
                          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-white/5 text-slate-300 transition flex items-center gap-1.5 font-semibold"
                        >
                          <FaEye /> <span>Cancel Changes</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Master fields */}
            <div
              className={`grid grid-cols-1 ${
                isUpdatingExisting &&
                (invoiceType === "SALE_RETURN" ||
                  invoiceType === "PURCHASE_RETURN")
                  ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto]"
                  : isUpdatingExisting && invoice.docType !== "INV"
                  ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto]"
                  : "sm:grid-cols-2"
              } gap-3 bg-slate-950/30 p-3 sm:p-4 rounded-xl border border-white/5 items-end`}
            >
              {/* Date */}
              <div className="flex flex-col gap-1.5 min-w-0">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                  <FaCalendarAlt className="text-slate-500" /> Date *
                </label>
                <input
                  type="datetime-local"
                  required
                  disabled={!editable}
                  value={invoiceData.invoiceDate}
                  onChange={(e) =>
                    setInvoiceData((prev) => ({
                      ...prev,
                      invoiceDate: e.target.value,
                    }))
                  }
                  className="w-full min-w-0 max-w-full bg-slate-950/60 border border-white/10 rounded-xl px-2 sm:px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition [color-scheme:dark]"
                />
              </div>

              {/* Partner */}
              {invoiceData.docType === "INV" && (
                <div className="flex flex-col gap-1.5 min-w-0">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                    <FaUser className="text-slate-500" /> Partner *
                  </label>
                  <div className="flex items-center gap-2 min-w-0">
                    <select
                      required
                      disabled={!editable}
                      value={invoiceData.partner.id}
                      onChange={(e) => handlePartnerChange(e.target.value)}
                      className="flex-1 w-0 min-w-0 bg-slate-950/60 border border-white/10 rounded-xl px-2 sm:px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <option
                        value=""
                        disabled
                        className="bg-slate-900 text-slate-500"
                      >
                        Select partner
                      </option>
                      {partners.map((p) => (
                        <option
                          key={p.id}
                          value={String(p.id)}
                          className="bg-slate-900"
                        >
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowPartnerModal(true)}
                      title="New Partner"
                      disabled={!editable}
                      className="h-10 w-10 shrink-0 rounded-xl bg-slate-600 hover:bg-slate-700 text-white flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <FaPlus className="text-sm" />
                    </button>
                  </div>
                </div>
              )}

              {invoiceData.partner?.id && (
                <>
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                      Email
                    </label>
                    <input
                      type="email"
                      value={invoiceData.email || ""}
                      disabled={!editable}
                      onChange={(e) =>
                        setInvoiceData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="Partner email"
                      className="w-full min-w-0 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 min-w-0">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                      Mobile
                    </label>
                    <input
                      type="tel"
                      value={invoiceData.mobile || ""}
                      disabled={!editable}
                      onChange={(e) =>
                        setInvoiceData((prev) => ({
                          ...prev,
                          mobile: e.target.value,
                        }))
                      }
                      placeholder="Partner mobile"
                      className="w-full min-w-0 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
                    />
                  </div>
                </>
              )}
              {/* Document type */}
              {((isUpdatingExisting && invoice?.docType !== "INV") ||
                currentMode === "MEMO") && (
                <div className="flex flex-col gap-1.5 min-w-0">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                    Doc type *
                  </label>
                  <div className="flex h-10 w-full sm:w-auto min-w-0 overflow-hidden rounded-xl border border-white/10 bg-slate-950/60">
                    {[
                      {
                        value: "CRE",
                        label: "CREDIT MEMO",
                        title: "Credit note — reduce qty / value",
                      },
                      {
                        value: "DEB",
                        label: "DEBIT MEMO",
                        title: "Debit note — increase qty / value",
                      },
                    ].map((option, i) => (
                      <label
                        key={option.value}
                        title={option.title}
                        className={`flex flex-1 sm:flex-none min-w-0 cursor-pointer items-center justify-center whitespace-nowrap px-2 sm:px-3 text-[10px] sm:text-[11px] font-bold transition ${
                          i > 0 ? "border-l border-white/10" : ""
                        } ${
                          invoiceData.docType === option.value
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "text-slate-400 hover:text-white"
                        }`}
                      >
                        <input
                          required
                          type="radio"
                          name="docType"
                          value={option.value}
                          checked={invoiceData.docType === option.value}
                          disabled={!editable}
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              docType: e.target.value,
                            }))
                          }
                          className="sr-only"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Line items */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-white text-sm flex items-center gap-1.5">
                  <FaBoxOpen className="text-emerald-500" /> Line Items
                </h3>
                {editable && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addNewItemLine}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition text-[11px]"
                    >
                      <FaPlus /> Add
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold flex items-center gap-2 shrink-0 shadow-lg shadow-violet-950/20"
                    >
                      <FaQrcode />{" "}
                      <span className="hidden sm:inline">Scan</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="border border-white/5 rounded-xl overflow-hidden bg-slate-950/10">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[700px]">
                    <thead>
                      <tr className="border-b border-white/5 bg-slate-950/40 text-slate-400 text-[10px] font-bold tracking-wider uppercase">
                        <th className="px-3 py-3 w-1/3">Product *</th>
                        <th className="px-2 py-3 w-20 text-center">Qty</th>
                        <th
                          className={`px-2 py-3 w-28 text-right ${
                            isSale ? "text-emerald-400" : ""
                          }`}
                        >
                          Selling Price
                        </th>
                        {isPurchase && (
                          <th className="px-2 py-3 w-28 text-right text-emerald-400">
                            Purchase Price
                          </th>
                        )}
                        <th className="px-2 py-3 w-20 text-center">
                          Discount (Fixed)
                        </th>
                        <th className="px-2 py-3 w-20 text-center">Batch</th>
                        <th className="px-2 py-3 w-20 text-center">
                          Expiry Date
                        </th>
                        {editable && <th className="px-3 py-3 w-12" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 font-mono text-slate-300">
                      {invoiceData.items.length === 0 ? (
                        <tr>
                          <td
                            colSpan={colCount}
                            className="text-center py-8 text-slate-500 font-sans italic"
                          >
                            No items yet — click "Add Item" to start.
                          </td>
                        </tr>
                      ) : (
                        invoiceData.items.map((item, index) => (
                          <tr key={index} className="hover:bg-white/[0.02]">
                            <td className="p-2 font-sans">
                              <select
                                required
                                disabled={!editable}
                                value={item.productId || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "productId",
                                    e.target.value
                                  )
                                }
                                className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-white text-xs focus:outline-none disabled:opacity-50 transition"
                              >
                                <option
                                  value=""
                                  disabled
                                  className="text-slate-600"
                                >
                                  Choose product
                                </option>
                                {products.map((p) => (
                                  <option key={p.id} value={String(p.id)}>
                                    {p.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                step="any"
                                min="0.001"
                                required
                                disabled={!editable}
                                value={item.quantity}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "quantity",
                                    e.target.value
                                  )
                                }
                                className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-white text-xs text-center focus:outline-none disabled:opacity-50 transition"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={!editable}
                                value={item.sellingPrice}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "sellingPrice",
                                    e.target.value
                                  )
                                }
                                className={`w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-xs text-right focus:outline-none disabled:opacity-50 transition ${
                                  isSale
                                    ? "text-emerald-400 font-bold"
                                    : "text-slate-400"
                                }`}
                              />
                            </td>
                            {isPurchase && (
                              <td className="p-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  disabled={!editable}
                                  value={item.purchasePrice}
                                  onChange={(e) =>
                                    handleItemFieldChange(
                                      index,
                                      "purchasePrice",
                                      e.target.value
                                    )
                                  }
                                  className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-xs text-right text-emerald-400 font-bold focus:outline-none disabled:opacity-50 transition"
                                />
                              </td>
                            )}
                            <td className="p-2">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                disabled={!editable}
                                value={item.discount}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "discount",
                                    e.target.value
                                  )
                                }
                                className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-white text-xs text-center focus:outline-none disabled:opacity-50 transition"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                disabled={!editable}
                                value={item.batchNumber}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "batchNumber",
                                    e.target.value
                                  )
                                }
                                className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-xs text-center focus:outline-none disabled:opacity-50 transition"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="date"
                                min={new Date().toISOString().split("T")[0]}
                                disabled={!editable}
                                value={item.expiryDate}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    index,
                                    "expiryDate",
                                    e.target.value
                                  )
                                }
                                className="w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-xs text-center focus:outline-none disabled:opacity-50 transition"
                              />
                            </td>
                            {editable && (
                              <td className="p-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => dropItemLine(index)}
                                  className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/10 rounded-lg transition"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Totals panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-white/10">
              {/* payment inputs */}
              <div className="bg-slate-950/20 p-4 rounded-xl border border-white/5 space-y-3 h-fit">
                <h4 className="font-bold text-white flex items-center gap-1.5 text-[11px] uppercase tracking-wider">
                  <FaCalculator className="text-slate-400" /> Payment settlement
                  Information
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {/* Payment Term */}
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                      Payment Term
                    </label>
                    <div className="flex items-center gap-4">
                      <label
                        className={`flex items-center gap-2 cursor-pointer ${
                          !fiscalFieldsEditable
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentTerm"
                          value="IMMIDIATE"
                          checked={invoiceData.paymentTerm === "IMMIDIATE"}
                          disabled={!editable}
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              paymentTerm: e.target.value,
                            }))
                          }
                          className="accent-emerald-500"
                        />
                        <span className="text-slate-300">Immediate</span>
                      </label>
                      <label
                        className={`flex items-center gap-2 cursor-pointer ${
                          !fiscalFieldsEditable
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentTerm"
                          value="CREDIT"
                          checked={invoiceData.paymentTerm === "CREDIT"}
                          disabled={!editable}
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              paymentTerm: e.target.value,
                            }))
                          }
                          className="accent-emerald-500"
                        />
                        <span className="text-slate-300">
                          On Account (Credit)
                        </span>
                      </label>
                    </div>
                  </div>

                  {invoiceData.paymentTerm === "IMMIDIATE" && (
                    <div>
                      <div className="flex flex-col gap-1.5 md:col-span-1">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                          <FaCalendarAlt className="text-slate-500" /> Payment
                          Method
                        </label>

                        <select
                          value={invoiceData.paymentMethod || "BANK"}
                          disabled={!editable}
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              paymentMethod: e.target.value,
                            }))
                          }
                          className="flex-1 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition [color-scheme:dark]"
                        >
                          <option value="CASH">CASH</option>
                          <option value="BANK">BANK</option>
                        </select>
                      </div>
                      {/* Payment Reference */}
                      <div className="flex flex-col gap-1.5 md:col-span-1">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                          <FaCalendarAlt className="text-slate-500" /> Payment
                          Reference
                          {invoiceData.paymentMethod === "BANK" && (
                            <span className="text-rose-400">*</span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={invoiceData.reference || ""}
                          disabled={!editable}
                          placeholder="FT00011"
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              reference: e.target.value,
                            }))
                          }
                          className="flex-1 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition [color-scheme:dark]"
                        />
                      </div>
                      {/* Bank Commission */}
                      <div className="flex flex-col gap-1.5 md:col-span-1">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                          <FaCalendarAlt className="text-slate-500" /> Bank
                          Commission
                          {invoiceData.paymentMethod === "BANK" && (
                            <span className="text-rose-400">*</span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={invoiceData.bankCommission ?? ""}
                          disabled={!editable}
                          placeholder="0.00"
                          onChange={(e) =>
                            setInvoiceData((prev) => ({
                              ...prev,
                              bankCommission: e.target.value,
                            }))
                          }
                          className="flex-1 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition [color-scheme:dark]"
                        />
                      </div>{" "}
                    </div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-950/40 border border-white/5 p-4 rounded-xl font-mono space-y-2 text-xs">
                <div className="flex justify-between text-slate-400">
                  Total Amount
                  <span>
                    {(
                      displayFinancials.grossAmount ??
                      displayFinancials.subtotal
                    ).toFixed(2)}
                  </span>
                </div>

                {showApplyExcise && (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <label className="flex items-start gap-3 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1 shrink-0"
                          checked={invoiceData.applyExcise}
                          onChange={(e) =>
                            handleApplyExciseChange(e.target.checked)
                          }
                          disabled={!editable}
                        />
                        <span className="text-slate-300 font-medium">
                          Apply Excise
                        </span>
                      </label>
                      <span className="font-mono text-slate-300 whitespace-nowrap">
                        {displayFinancials.excise.toFixed(2)}
                      </span>
                    </div>

                    {invoiceData.applyExcise && (
                      <div className="ml-7 space-y-1">
                        <div className="flex justify-between text-slate-400">
                          <span>Taxable Excise</span>
                          <span>
                            {displayFinancials.taxableExcise.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Non-taxable Excise</span>
                          <span>
                            {displayFinancials.nonTaxableExcise.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {displayFinancials.discount > 0 && (
                  <div className="flex justify-between text-slate-400">
                    Discount
                    <span>{displayFinancials.discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-slate-400">
                  Taxable
                  <span>{displayFinancials.taxableAmount.toFixed(2)}</span>
                </div>
                {displayFinancials.nonTaxableAmount > 0 && (
                  <div className="flex justify-between text-slate-400">
                    Non-taxable
                    <span>{displayFinancials.nonTaxableAmount.toFixed(2)}</span>
                  </div>
                )}

                {showApplyVat && (
                  <div className="flex items-start justify-between gap-4">
                    <label className="flex items-start gap-3 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 shrink-0"
                        checked={invoiceData.applyVat}
                        onChange={(e) => handleApplyVatChange(e.target.checked)}
                        disabled={!editable}
                      />
                      <div className="flex flex-col">
                        <span className="text-slate-300 font-medium">
                          {taxLabel}
                        </span>
                      </div>
                    </label>
                    <span className="font-mono text-slate-300 whitespace-nowrap">
                      + {displayFinancials.tax.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="h-px bg-white/10 my-2" />
                <div className="flex justify-between text-white font-bold">
                  <span>Gross Invoice Total</span>
                  <span className="text-emerald-400">
                    {displayFinancials.total.toFixed(2)} ETB
                  </span>
                </div>
                {/* Income WH */}
                {canShowIncomeWithhold && (
                  <div className="flex items-start justify-between gap-4">
                    <label
                      className={`flex items-start gap-3 flex-1 ${
                        withholdingEligible
                          ? "cursor-pointer"
                          : "opacity-60 cursor-not-allowed"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 shrink-0"
                        disabled={!withholdingEligible || !editable}
                        checked={invoiceData.applyWithhold}
                        onChange={(e) =>
                          handleApplyWithholdChange(e.target.checked)
                        }
                      />
                      <div className="flex flex-col">
                        <span className="text-slate-300 font-medium">
                          {isPurchase
                            ? "Apply Income Tax Withholding"
                            : "Customer Withholds Income Tax"}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          Rate:{" "}
                          {partnerIsBusiness
                            ? withholdRate
                            : INDIVIDUAL_WITHHOLD_RATE}
                          %
                        </span>
                        {!withholdingEligible && (
                          <span className="text-[10px] text-amber-400">
                            Threshold not reached (Goods ≥ 20 000 ETB or
                            Services ≥ 10 000 ETB)
                          </span>
                        )}
                      </div>
                    </label>
                    <span className="font-mono text-slate-300 whitespace-nowrap">
                      - {displayFinancials.withhold.toFixed(2)}
                    </span>
                  </div>
                )}
                {/* VAT WH */}
                {canShowVatWithhold && (
                  <div className="flex items-start justify-between gap-4">
                    <label
                      className={`flex items-start gap-3 flex-1 ${
                        invoiceData.applyVat
                          ? "cursor-pointer"
                          : "opacity-60 cursor-not-allowed"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 shrink-0"
                        disabled={!invoiceData.applyVat || !editable}
                        checked={invoiceData.applyVatWithhold}
                        onChange={(e) =>
                          handleApplyVatWithholdChange(e.target.checked)
                        }
                      />
                      <div className="flex flex-col">
                        <span className="text-slate-300 font-medium">
                          {isPurchase
                            ? "Apply VAT Withholding"
                            : "Customer Withholds VAT"}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {vatWithholdRate}% of VAT
                        </span>
                        {!invoiceData.applyVat && (
                          <span className="text-[10px] text-amber-400">
                            Enable VAT first
                          </span>
                        )}
                      </div>
                    </label>
                    <span className="font-mono text-slate-300 whitespace-nowrap">
                      - {displayFinancials.vatWithhold.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="h-px bg-white/10 my-2" />
                <div className="flex justify-between text-white font-bold">
                  <span>Net Cash Payable</span>
                  <span className="text-emerald-400">
                    {displayFinancials.payable.toFixed(2)} ETB
                  </span>
                </div>

                {/* Receipt / Fiscal Reference */}
                {(() => {
                  const isSaleSide =
                    invoiceData.type === "SALE" ||
                    invoiceData.type === "SALE_RETURN";
                  const isPurchaseSide =
                    invoiceData.type === "PURCHASE" ||
                    invoiceData.type === "PURCHASE_RETURN";

                  const effectiveFiscalType = isPurchaseSide
                    ? invoiceData.fiscalType || "MANUAL"
                    : fiscalType;

                  const hideAllRefs =
                    isSaleSide && effectiveFiscalType === "EIMS";

                  const showIrnField =
                    !hideAllRefs &&
                    (effectiveFiscalType === "MANUAL" ||
                      effectiveFiscalType === "MACHINE" ||
                      effectiveFiscalType === "EIMS");

                  const showMrcField = effectiveFiscalType === "MACHINE";

                  const irnLabel =
                    effectiveFiscalType === "MANUAL"
                      ? "Manual Invoice / Receipt"
                      : effectiveFiscalType === "MACHINE"
                      ? "FS Number"
                      : "IRN";

                  const irnPlaceholder =
                    effectiveFiscalType === "MANUAL"
                      ? "Manual Invoice Number (Required)"
                      : effectiveFiscalType === "MACHINE"
                      ? "FS Number (Required)"
                      : "IRN (Required)";

                  return (
                    <div className="mt-4 pt-3 border-t border-white/10 w-full space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1.5">
                          <FaHashtag className="text-emerald-500" />
                          Supplier Fiscalization Mode
                          {!isPurchaseSide && (
                            <span className="ml-1 text-slate-600 normal-case font-normal">
                              * {fiscalType}
                            </span>
                          )}
                        </div>

                        {isPurchaseSide && fiscalFieldsEditable && (
                          <select
                            value={invoiceData.fiscalType || "MANUAL"}
                            onChange={(e) =>
                              setInvoiceData((prev) => ({
                                ...prev,
                                fiscalType: e.target.value,
                                irn: "",
                                mrc: "",
                                newIrn: e.target.value === "EIMS",
                              }))
                            }
                            className="bg-slate-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-white focus:outline-none focus:border-emerald-500/50"
                          >
                            <option value="EIMS">EIMS</option>
                            <option value="MANUAL">Manual</option>
                            <option value="MACHINE">Machine</option>
                          </select>
                        )}
                      </div>

                      {hideAllRefs && (
                        <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider text-slate-500">
                          <span>IRN will be generated by EIMS</span>
                        </div>
                      )}

                      {(showIrnField || showMrcField) && (
                        <div
                          className={`grid gap-3 ${
                            showMrcField ? "md:grid-cols-2" : "grid-cols-1"
                          }`}
                        >
                          {showIrnField && (
                            <div className="relative">
                              <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-amber-400 pointer-events-none" />
                              <input
                                type="text"
                                disabled={!editable}
                                required={effectiveFiscalType !== "EIMS"}
                                value={invoiceData.irn || ""}
                                onChange={(e) => {
                                  setInvoiceData((prev) => ({
                                    ...prev,
                                    irn: e.target.value,
                                    newIrn: false,
                                  }));
                                }}
                                placeholder={irnPlaceholder}
                                title={irnLabel}
                                className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                              />
                            </div>
                          )}

                          {showMrcField && (
                            <div className="relative">
                              <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-emerald-400 pointer-events-none" />
                              <input
                                type="text"
                                disabled={!editable}
                                required
                                value={invoiceData.mrc || ""}
                                onChange={(e) => {
                                  setInvoiceData((prev) => ({
                                    ...prev,
                                    mrc: e.target.value,
                                    newIrn: false,
                                  }));
                                }}
                                placeholder="MRC (Required)"
                                className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {(invoiceData.applyWithhold &&
                        displayFinancials.withhold > 0) ||
                      (invoiceData.applyVatWithhold &&
                        displayFinancials.vatWithhold > 0) ? (
                        <div className="mt-3 pt-3 border-t border-white/10 w-full space-y-3">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1.5">
                            <FaHashtag className="text-amber-400" />
                            Withholding Receipt Numbers
                          </div>

                          <div
                            className={`grid gap-3 ${
                              invoiceData.applyWithhold &&
                              displayFinancials.withhold > 0 &&
                              invoiceData.applyVatWithhold &&
                              displayFinancials.vatWithhold > 0
                                ? "md:grid-cols-2"
                                : "grid-cols-1"
                            }`}
                          >
                            {invoiceData.applyWithhold &&
                              displayFinancials.withhold > 0 && (
                                <div className="relative">
                                  <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-amber-400 pointer-events-none" />
                                  <input
                                    type="text"
                                    disabled={!editable}
                                    value={invoiceData.whReceiptNumber || ""}
                                    onChange={(e) =>
                                      setInvoiceData((prev) => ({
                                        ...prev,
                                        whReceiptNumber: e.target.value,
                                      }))
                                    }
                                    placeholder="TWTH Receipt No."
                                    title="Income Tax Withholding Receipt Number"
                                    className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                                  />
                                </div>
                              )}

                            {invoiceData.applyVatWithhold &&
                              displayFinancials.vatWithhold > 0 && (
                                <div className="relative">
                                  <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-emerald-400 pointer-events-none" />
                                  <input
                                    type="text"
                                    disabled={!editable}
                                    value={invoiceData.vatWhReceiptNumber || ""}
                                    onChange={(e) =>
                                      setInvoiceData((prev) => ({
                                        ...prev,
                                        vatWhReceiptNumber: e.target.value,
                                      }))
                                    }
                                    placeholder="VWTH Receipt No."
                                    title="VWTH Receipt"
                                    className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-all disabled:opacity-50"
                                  />
                                </div>
                              )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </div>
          </form>
        )}
      </div>

      {showScanner && (
        <QrScannerModal
          onClose={() => setShowScanner(false)}
          onDetect={lookupAndAdd}
        />
      )}

      <PartnerQuickModal
        open={showPartnerModal}
        type={
          invoiceData.type === "SALE" || invoiceData.type === "SALE_RETURN"
            ? "CUSTOMER"
            : "SUPPLIER"
        }
        loading={savingPartner}
        onClose={() => setShowPartnerModal(false)}
        onSave={handleCreatePartner}
      />
    </div>
  );
};

export default InvoiceCreate;
